import { useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DragEndParams } from "react-native-draggable-flatlist";
import { useDebouncedCallback } from "use-debounce";

import LeftColumnView from "./PlayerDetailLeftView";

import ManuallyAddPlayerModal from "@/components/organisms/ManuallyAddPlayerModal";
import {
  OrderByDirection,
  SortField,
  usePlayerSearchSearchPlayersLiteQuery,
  useWatchlistListWatchlistPlayersQuery,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import { useCustomMetricSortVariables } from "@/hooks/useCustomMetricSortVariables";
import { useFilter } from "@/hooks/useFilter";
import useMobileScreen from "@/hooks/useMobileScreen";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { focusSelectedVar, searchTextVar } from "@/stores/isFocusSearchInput";
import { watchListSelectedVar } from "@/stores/player";
import { watchlistSortVar } from "@/stores/selectionSort";
import { myRosterSelectedVar } from "@/stores/watchlistSelection";
import { isTransfermarktUrl } from "@/utils/transfermarktUrl";
/**
 * Props for PlayerDetailLeftContainer component
 */
interface LeftColumnContainerProps {
  loading: boolean;
  refreshing: boolean;
  loadWatchList?: boolean;
  players: PlayerCardData[];
  watchlists?: WatchlistWatchlistMetaFragment[];
  heightRecommendation?: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  onCallbackOpen?: () => void;
  activeWatchlist?: WatchlistWatchlistMetaFragment | null;
  onDragEnd?: (params: DragEndParams<PlayerCardData>) => void;
}

/**
 * Container component for player detail left column with search, filter, and watchlist selection.
 *
 * Watchlist switching updates reactive variables in-place without navigation,
 * allowing the sidebar to refresh via `useReactiveVar(watchListSelectedVar)` while
 * preserving the user's current player detail view and active tab state.
 * @param root0 - Component props
 * @param root0.loading - Whether player data is loading
 * @param root0.refreshing - Whether list is being refreshed via pull-to-refresh
 * @param root0.loadWatchList - Whether watchlist data is loading
 * @param root0.players - Array of player fragments to display in list
 * @param root0.watchlists - Array of available watchlists for selection
 * @param root0.heightRecommendation - Recommended height for list container padding
 * @param root0.onLoadMore - Callback invoked when scrolling near end to load more players
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.onCallbackOpen - Callback invoked when watchlist selection opens
 * @param root0.activeWatchlist - Currently selected watchlist to display players from
 * @param root0.onDragEnd - Callback invoked when drag-and-drop reordering ends
 */
const LeftColumnContainer = ({
  activeWatchlist,
  ...props
}: LeftColumnContainerProps) => {
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const isManualAddPlayerEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.MANUALLY_ADD_PLAYER) === true;
  const searchValueVar = useReactiveVar(searchTextVar);
  const focusSelected = useReactiveVar(focusSelectedVar);
  const [searchValue, setSearchValue] = useState<string>("");
  const sortValues = useReactiveVar(watchlistSortVar);
  const isSmallScreen = useMobileScreen();
  const watchListSelected = useReactiveVar(watchListSelectedVar);
  const isMyRosterSelected = useReactiveVar(myRosterSelectedVar);

  // Get custom metric sort variables using shared hook
  const { isCustomSort, sortVariables } = useCustomMetricSortVariables(
    sortValues,
    isSortPlayerEnable
  );

  // Derive selected watchlist from reactive var or fall back to prop
  const selectedWatchlist = useMemo(
    () =>
      props.watchlists?.find(w => w.id === watchListSelected) ??
      activeWatchlist ??
      null,
    [props.watchlists, watchListSelected, activeWatchlist]
  );

  // Query for watchlist players when a watchlist is selected
  const {
    data: watchlistPlayersData,
    loading: loadingWatchlistPlayers,
    fetchMore: fetchMoreWatchlistPlayers,
  } = useWatchlistListWatchlistPlayersQuery({
    variables: {
      watchlistId: watchListSelected ?? "",
      ...sortVariables,
    },
    skip: !watchListSelected,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const watchlistPlayers = useMemo(
    () =>
      watchlistPlayersData?.listWatchlistPlayers?.edges?.map(
        edge => edge?.node
      ) ?? [],
    [watchlistPlayersData?.listWatchlistPlayers?.edges]
  );

  const watchlistPageInfo = useMemo(
    () => watchlistPlayersData?.listWatchlistPlayers?.pageInfo,
    [watchlistPlayersData?.listWatchlistPlayers?.pageInfo]
  );

  const handleLoadMoreWatchlist = useCallback(() => {
    const { hasNextPage, endCursor } = watchlistPageInfo ?? {};
    if (hasNextPage && !loadingWatchlistPlayers) {
      fetchMoreWatchlistPlayers({ variables: { after: endCursor } });
    }
  }, [watchlistPageInfo, fetchMoreWatchlistPlayers, loadingWatchlistPlayers]);

  // searchPlayers query doesn't support custom stat sorting - fall back to GPR
  const searchSortBy =
    isSortPlayerEnable && !!sortValues?.sortOrder
      ? isCustomSort
        ? SortField.Gpr
        : (sortValues?.sortBy as SortField)
      : undefined;

  const {
    data: playerSearch,
    loading: loadingSearch,
    fetchMore: fetchMoreSearch,
  } = usePlayerSearchSearchPlayersLiteQuery({
    variables: {
      query: searchValue,
      sortBy: searchSortBy,
      sortOrder: isSortPlayerEnable
        ? (sortValues?.sortOrder as OrderByDirection)
        : undefined,
    },
    // Never text-search a Transfermarkt URL — it resolves via the ID lookup
    // in the shared Searchbar (SE-3599).
    skip: !searchValue || isTransfermarktUrl(searchValue),
    notifyOnNetworkStatusChange: true,
  });
  const handleDebounceSearch = useDebouncedCallback(setSearchValue, 300);

  const handleSelectWatchList = useCallback((watchListId: string) => {
    watchListSelectedVar(watchListId);
    myRosterSelectedVar(false);
  }, []);

  const handleOpenMyRoster = useCallback(() => {
    myRosterSelectedVar(true);
    watchListSelectedVar(undefined);
  }, []);

  const pageInfoSearch = useMemo(
    () => playerSearch?.searchPlayers?.pageInfo,
    [playerSearch?.searchPlayers?.pageInfo]
  );

  const handleLoadMoreSearch = useCallback(() => {
    const { hasNextPage, endCursor } = pageInfoSearch ?? {};
    if (hasNextPage && !loadingSearch) {
      fetchMoreSearch({ variables: { after: endCursor } });
    }
  }, [pageInfoSearch, fetchMoreSearch, loadingSearch]);

  const {
    loading: loadingFilter,
    players: playerFilters,
    handleLoadMore,
  } = useFilter();

  // Manual player modal state (SE-3733 entry point 5)
  const [isManualAddOpen, setIsManualAddOpen] = useState(false);

  const handleManualAddPlayer = useCallback(() => {
    setIsManualAddOpen(true);
  }, []);

  const handleManualAddClose = useCallback(() => {
    setIsManualAddOpen(false);
  }, []);

  const dataDisplay = (() => {
    switch (focusSelected.type) {
      case "search":
        return {
          loading: loadingSearch,
          players:
            playerSearch?.searchPlayers?.edges.map(
              edge => edge.node as PlayerCardData
            ) || [],
          onLoadMore: handleLoadMoreSearch,
          searchQuery: searchValue,
          onManualAddPlayer: isManualAddPlayerEnabled
            ? handleManualAddPlayer
            : undefined,
        };
      case "filter":
        return {
          loading: loadingFilter,
          players: playerFilters as PlayerCardData[],
          onLoadMore: handleLoadMore,
        };
      default:
        // When a watchlist is selected (and My Roster is not), show watchlist players
        // Otherwise show the default roster players from props
        if (watchListSelected && !isMyRosterSelected) {
          return {
            loading: loadingWatchlistPlayers,
            players: watchlistPlayers as PlayerCardData[],
            onLoadMore: handleLoadMoreWatchlist,
          };
        }
        return props;
    }
  })();

  useEffect(() => {
    handleDebounceSearch(searchValueVar);
  }, [handleDebounceSearch, searchValueVar]);

  return (
    <>
      <LeftColumnView
        {...props}
        {...dataDisplay}
        onDragEnd={
          focusSelected.type === "default" ? props.onDragEnd : undefined
        }
        selectedWatchlist={selectedWatchlist}
        onSelectWatchlist={handleSelectWatchList}
        onOpenMyRoster={handleOpenMyRoster}
        isSmallScreen={isSmallScreen}
      />
      {isManualAddPlayerEnabled ? (
        <ManuallyAddPlayerModal
          isOpen={isManualAddOpen}
          onClose={handleManualAddClose}
          searchText={searchValue}
        />
      ) : null}
    </>
  );
};

export default LeftColumnContainer;
